import { CheckCircle, MapPin, Coffee } from 'lucide-react';
import PageWrapper from '@/components/PageWrapper';
import ImageCarousel from '@/components/ImageCarousel';
import { FadeUp, SlideLeft, SlideRight, StaggerContainer, StaggerItem } from '@/components/ScrollReveal';

const StudioPage = () => {
  const studioFeatures = [
    'Two purpose-built spaces: a calm, private main studio and a dedicated Cadillac room',
    'Quiet, refined environment for focused one-on-one sessions',
    'Thoughtfully designed amenities including private bathroom, tea, coffee, and cold drinks',
    'High-ceiling Cadillac room for unrestricted advanced exercises',
  ];

  const images = [
    { src: '/images/THE STUDIO.jpg', alt: 'Main Studio', caption: 'Main Studio' },
    { src: '/images/The Studio Wide.jpg', alt: 'Studio Wide', caption: 'Studio Space' },
    { src: '/images/REFEORMER.jpg', alt: 'Reformer', caption: 'Reformer' },
    { src: '/images/CAD.jpg', alt: 'Cadillac Room', caption: 'Cadillac Room' },
  ];

  return (
    <PageWrapper>
      {/* Hero */}
      <section data-testid="studio-hero" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">Services</p>
            <h1 className="font-heading text-5xl sm:text-6xl text-stone-900 leading-[1.1] tracking-tight">
              The <span className="italic">Studio</span>
            </h1>
            <div className="flex items-center gap-2 mt-4 text-stone-500">
              <MapPin size={16} strokeWidth={1.5} />
              <span className="text-sm">Kingsland, Auckland</span>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <StaggerContainer className="grid md:grid-cols-2 gap-6">
            {studioFeatures.map((feature, index) => (
              <StaggerItem key={index}>
                <div className="flex items-start gap-4 p-6 bg-white border border-stone-200">
                  <CheckCircle className="text-stone-900 flex-shrink-0 mt-1" size={20} strokeWidth={1.5} />
                  <p className="text-stone-600 leading-relaxed font-light">{feature}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Image Carousel */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <ImageCarousel images={images} />
          </FadeUp>
        </div>
      </section>

      {/* Main Studio */}
      <section data-testid="main-studio" className="py-24 md:py-32 bg-stone-900 text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <SlideLeft>
              <div className="image-zoom aspect-[4/3]">
                <img
                  src="/images/THE STUDIO.jpg"
                  alt="Main Studio"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
            </SlideLeft>

            <SlideRight>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">Primary Space</p>
              <h2 className="font-heading text-4xl sm:text-5xl text-white leading-[1.1] tracking-tight">
                The Main <span className="italic">Studio</span>
              </h2>
              <p className="mt-8 text-stone-300 leading-relaxed font-light">
                The main studio is quiet and grounded — a private, welcoming space designed 
                for focused one-on-one sessions. Natural light, clean lines, and considered 
                details create an environment that feels relaxed yet purposeful.
              </p>
              <div className="mt-8 flex items-start gap-4 p-4 border border-stone-700">
                <Coffee className="text-stone-400 flex-shrink-0 mt-1" size={20} strokeWidth={1.5} />
                <p className="text-stone-400 text-sm font-light">
                  Private bathroom facilities, tea, coffee, and cold drinks available.
                </p>
              </div>
            </SlideRight>
          </div>
        </div>
      </section>

      {/* Cadillac Studio */}
      <section data-testid="cadillac-studio" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <SlideLeft className="order-2 lg:order-1">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">Advanced Training</p>
              <h2 className="font-heading text-4xl sm:text-5xl text-stone-900 leading-[1.1] tracking-tight">
                Cadillac <span className="italic">Studio</span>
              </h2>
              <p className="mt-8 text-stone-600 leading-relaxed font-light">
                A dedicated Cadillac room designed for advanced and specialised work. 
                The high ceilings allow complete freedom of movement, including inverted 
                and upside-down positions without restriction.
              </p>
              <p className="mt-6 text-stone-500 italic font-light">
                Together, the two spaces offer both comfort and capability — calm yet fully 
                equipped for progressive Pilates practice.
              </p>
            </SlideLeft>

            <SlideRight className="order-1 lg:order-2">
              <div className="image-zoom aspect-[4/3]">
                <img
                  src="/images/HERO CADI CLOSER SHOT.jpg"
                  alt="Cadillac Studio"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
            </SlideRight>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <FadeUp>
            <MapPin className="mx-auto mb-4 text-stone-400" size={32} strokeWidth={1} />
            <h2 className="font-heading text-3xl text-stone-900">Kingsland, Auckland</h2>
            <p className="mt-4 text-stone-500 font-light">
              Full address provided after booking. Free two-hour street parking available.
            </p>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
};

export default StudioPage;
